import { constants } from '@config';

import { cellSizeMin, defaultFontSize, directionHorizontal, directionVertical } from './table';
import tableQuery from './table-query';

// Represents a table cell
export default class TableCell {
  constructor () {
    /**
     * @type {SVGGElement}
     */
    this.cellElement = undefined;

    /**
     * @type {HTMLCanvasElement}
     */
    this.canvas = INKIVE.EditorFacade.editor.canvas;
  }

  // Create a new cell from given data
  fromData (rowElement, data) {
    // Default font size
    const fontSize = Math.min(defaultFontSize, data.height * 0.8);

    this.cellElement = this.canvas.addSvgElementFromJson({
      element: 'g',
      attr: {
        class: `table-cell index-r-${data.rowIndex} index-c-${data.columnIndex} ${data.mergedData ?? ''}`
      }
    });

    const gTextArea = this.canvas.addSvgElementFromJson({
      element: 'g',
      attr: {
        id: this.canvas.getNextId(),
        class: 'textarea vertical-center'
      }
    });

    const cellRect = this.canvas.addSvgElementFromJson({
      element: 'rect',
      attr: {
        x: data.x,
        y: data.y,
        width: data.width,
        height: data.height,
        fill: data.fill
      }
    });

    const gText = this.canvas.addSvgElementFromJson({
      element: 'g',
      attr: {
        class: `text line-margin-${fontSize} right-margin-${fontSize} first-line-margin-${fontSize}`,
        fill: '#000000',
        stroke: 'none',
        'font-family': 'Lato',
        'font-size': fontSize,
        'text-anchor': 'start',
        'stroke-width': '0',
        'font-weight': '400'
      }
    });

    const text = this.canvas.addSvgElementFromJson({
      element: 'text',
      attr: {
        class: ' blank eol',
        x: data.x + data.width / 2,
        y: data.y + (data.height + fontSize) / 2
      }
    });

    text.textContent = constants.text.noBreakSpace;
    gText.appendChild(text);
    gTextArea.appendChild(cellRect);
    gTextArea.appendChild(gText);
    this.cellElement.appendChild(gTextArea);

    const where = rowElement.querySelector(`g.table-cell.index-c-${data.columnIndex + 1}`);

    if (where) {
      rowElement.insertBefore(this.cellElement, where);
    } else {
      rowElement.appendChild(this.cellElement);
    }
  }

  // Use an existing element
  fromElement (cellElement) {
    this.cellElement = cellElement;
  }

  // Set cell indices (used for shifing cell on insertion and deletion)
  setIndices (rowIndex, columnIndex) {
    const classList = [...this.cellElement.classList];
    const newClassList = [];

    classList.forEach(className => {
      if (className.startsWith('index-r')) {
        newClassList.push(`index-r-${rowIndex}`);
      } else if (className.startsWith('index-c')) {
        newClassList.push(`index-c-${columnIndex}`);
      } else {
        newClassList.push(className);
      }
    });

    this.cellElement.setAttribute('class', newClassList.join(' '));
  }

  // True if the cell size (width or height) can be changed by delta (positive or negative)
  // direction is either 'h' or 'v'
  confirmSizeChange (delta, direction) {
    if (delta >= 0) return true;

    const rect = this.cellElement.querySelector('rect');
    const textElements = this.cellElement.querySelectorAll('text');
    const isVerticalDirection = this.checkVerticalDirection(direction);

    if (!isVerticalDirection) {
      const width = parseFloat(rect.getAttribute('width'));
      return (width + delta) > cellSizeMin.width;
    } else {
      const firstTextElement = textElements[0];
      const lastTextElement = textElements[textElements.length - 1];
      const firstTextY = parseFloat(firstTextElement.getAttribute('y'));
      const lastTextY = parseFloat(lastTextElement.getAttribute('y'));
      const cellPadding = tableQuery.getCellPadding(this.cellElement);
      const textHeight = lastTextY - firstTextY + firstTextElement.getBBox().height + cellPadding.top + cellPadding.bottom;
      const height = parseFloat(rect.getAttribute('height'));
      const minHeight = Math.max(textHeight, cellSizeMin.height);
      return (height + delta) > minHeight;
    }
  }

  // Resize the cell by delta, direction is either 'h' or 'v'
  /**
   *
   * @param {number} delta
   * @param {'v' | 'h'} direction
   * @param {boolean} isCurrentCell
   * @param {boolean} isCurrentRow
   * @param {boolean} allowResizeBelowMinValue
   */
  resize (delta, direction, isCurrentCell = false, isCurrentRow = false, allowResizeBelowMinValue = true) {
    const rect = this.cellElement.querySelector('rect');
    const row = this.cellElement.parentElement;

    const isVerticalDirection = this.checkVerticalDirection(direction);
    const attributeName = isVerticalDirection ? 'height' : 'width';
    const value = parseFloat(rect.getAttribute(attributeName));
    const minContentAttribute = `data-min-content-${attributeName}`;
    const minContentValue = parseFloat(row.getAttribute(minContentAttribute));

    if (isNaN(minContentValue)) {
      row.setAttribute(minContentAttribute, value);
    }

    if (!isNaN(minContentValue) && !allowResizeBelowMinValue && value + delta < minContentValue) {
      delta = minContentValue - value;
    }

    rect.setAttribute(attributeName, value + delta);

    if (allowResizeBelowMinValue) {
      row.setAttribute(minContentAttribute, value + delta);
    }

    if (isVerticalDirection && !isCurrentCell) {
      const verticalAlignment = this.getCellVerticalAlignment();
      // If the cell is vertically center aligned, we need to move the text by half of the delta
      let factor = verticalAlignment.includes('center') ? 0.5 : 1;
      if (isCurrentRow && verticalAlignment.includes('top')) factor = 0;

      const textElements = this.cellElement.querySelectorAll('text');
      const highlightRectElements = [...this.cellElement.querySelectorAll('rect.highlight-rect')];

      textElements.forEach(textElement => {
        const currentY = parseFloat(textElement.getAttribute('y'));
        textElement.setAttribute('y', currentY + delta * factor);
      });

      highlightRectElements.forEach(rect => {
        const currentY = parseFloat(rect.getAttribute('y'));
        rect.setAttribute('y', currentY + delta * factor);
      });
    }
  }

  // Displace the cell by delta, direction is either 'h' or 'v'
  move (delta, direction) {
    const rect = this.cellElement.querySelector('rect');

    const isVerticalDirection = this.checkVerticalDirection(direction);
    const attributeName = isVerticalDirection ? 'y' : 'x';

    let value = parseFloat(rect.getAttribute(attributeName));
    rect.setAttribute(attributeName, value + delta);

    // A cell may contain multiple texts
    const textElements = this.cellElement.querySelectorAll('text');
    const highlightRectElements = [...this.cellElement.querySelectorAll('rect.highlight-rect')];

    textElements.forEach(textElement => {
      value = parseFloat(textElement.getAttribute(attributeName));
      textElement.setAttribute(attributeName, value + delta);
    });

    direction === directionVertical && highlightRectElements.forEach(rect => {
      value = parseFloat(rect.getAttribute(attributeName));
      rect.setAttribute(attributeName, value + delta);
    });
  }

  setTextAlignment (horizontal, vertical) {
    const textareaElement = this.cellElement.querySelector('g.textarea');

    textareaElement.classList.forEach(className => {
      if (className.match(/^vertical-.*/)) {
        textareaElement.classList.remove(className);
      }
    });

    textareaElement.classList.add(`vertical-${vertical}`);

    const textArea = new INKIVE.Text.Textarea().setDOM(textareaElement);
    textArea.text.setValues({ text_anchor: horizontal });

    INKIVE.EditorFacade.editor.canvas.runExtensions('renderTextArea', { textArea, resize: false });
  }

  getCellVerticalAlignment () {
    let verticalAlignment = '';
    const textareaElement = this.cellElement.querySelector('g.textarea');
    textareaElement.classList.forEach(className => {
      if (className.match(/^vertical-.*/)) {
        verticalAlignment = className;
      }
    });

    return verticalAlignment;
  }

  checkVerticalDirection (direction) {
    const angle = svgedit.utilities.getRotationAngle(this.cellElement.querySelector('g.textarea'));

    if (angle === 90 || angle === 270) {
      return direction !== directionVertical;
    }
    return direction === directionVertical;
  }
}
